import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Shield, ShieldCheck, ShieldOff, Loader2, AlertTriangle, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { swatidService } from '@/services';
import type { SwatidOperationMode } from '@/constants/swatid';
import { useSwatidSupervision } from '@/hooks/useSwatidController';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface SwatidSupervisionSectionProps {
  controllerId: string;
  currentMode: SwatidOperationMode;
  isOnline: boolean;
  onRequestLocalMode?: () => void;
}

export function SwatidSupervisionSection({
  controllerId,
  currentMode,
  isOnline,
  onRequestLocalMode,
}: SwatidSupervisionSectionProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useSwatidSupervision(controllerId, true);

  const supervisionMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const res = await swatidService.setSupervision(controllerId, { enabled });
      if (!res.success) throw new Error(res.error?.message || 'Error al actualizar supervisión');
      return res.data;
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['swatid-supervision', controllerId] });
      await queryClient.invalidateQueries({ queryKey: ['controllers'] });
      toast({
        title: result?.supervisionMode ? 'Supervisión activada' : 'Supervisión desactivada',
        description: result?.supervisionMode
          ? 'El backend monitorizará el cumplimiento de programaciones en modo Local.'
          : 'La monitorización automática se ha detenido.',
      });
    },
    onError: (e: Error) => {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {error instanceof Error ? error.message : 'Error al cargar supervisión'}
        </AlertDescription>
      </Alert>
    );
  }

  const supervisionEnabled = data?.supervisionMode ?? false;
  const isActive = data?.requirements?.isActive ?? false;
  const isLocal = currentMode === 'local';

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            {isActive ? (
              <ShieldCheck className="w-5 h-5 text-success" />
            ) : supervisionEnabled ? (
              <Shield className="w-5 h-5 text-amber-500" />
            ) : (
              <ShieldOff className="w-5 h-5 text-muted-foreground" />
            )}
            Modo Local + Supervisión
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
            <div className="space-y-1">
              <Label htmlFor="swatid-supervision">Supervisión automática</Label>
              <p className="text-xs text-muted-foreground">
                Monitoriza cada 60s si los relés siguen las programaciones locales e interviene si
                detecta desviaciones.
              </p>
            </div>
            <Switch
              id="swatid-supervision"
              checked={supervisionEnabled}
              disabled={supervisionMutation.isPending || !isOnline}
              onCheckedChange={(v) => supervisionMutation.mutate(v)}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <Badge variant={isActive ? 'default' : 'secondary'} className={cn(isActive && 'bg-success')}>
              {isActive ? 'Supervisión activa' : supervisionEnabled ? 'Pendiente (requiere Local)' : 'Inactiva'}
            </Badge>
            <Badge variant="outline">Modo: {currentMode}</Badge>
            {data?.todaySchedules != null && (
              <Badge variant="outline">Prog. hoy: {data.todaySchedules}</Badge>
            )}
            {data?.todayEvents != null && (
              <Badge variant="outline">Eventos hoy: {data.todayEvents}</Badge>
            )}
          </div>

          {supervisionEnabled && !isLocal && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="space-y-2">
                <p>
                  La supervisión está habilitada pero el dispositivo no está en modo{' '}
                  <strong>Local</strong>. Se activará al cambiar a Local.
                </p>
                {onRequestLocalMode && (
                  <Button size="sm" variant="outline" onClick={onRequestLocalMode}>
                    Cambiar a modo Local
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          )}

          {!isOnline && (
            <Alert variant="destructive">
              <AlertDescription>
                Dispositivo offline. La supervisión requiere que la controladora esté online.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Info className="w-4 h-4" />
            ¿Qué hace la supervisión?
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>En modo <strong>Local</strong>, el dispositivo ejecuta sus programaciones horarias.</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Si un riego programado no inicia → interviene (modo Remota temporal, enciende relé).</li>
            <li>Si un relé riega fuera de horario → interviene (apaga y espera 15 min).</li>
            <li>Tras corregir, vuelve automáticamente a modo Local.</li>
          </ul>
          <p className="text-xs pt-1">
            Requisitos: modo Local + supervisión ON + programaciones configuradas + dispositivo online.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
